/**
 * Virtuaroo client entry point.
 *
 * Boot flow:
 *   1. Show loading screen + start engine init in parallel
 *   2. Wait for init complete, mount disclaimer, show spawn picker
 *   3. On spawn pick: drop player into the chosen zone, mount HUD/chat/minimap
 *   4. Render loop runs forever
 */

import { Renderer } from '@/core/Renderer';
import { mountDisclaimer } from '@/ui/Disclaimer';
import { LoadingScreen } from '@/ui/LoadingScreen';
import { SpawnPicker } from '@/ui/SpawnPicker';
import { HUD } from '@/ui/HUD';
import { ChatPanel } from '@/ui/ChatPanel';
import { Minimap } from '@/ui/Minimap';
import { Onboarding } from '@/ui/Onboarding';
import { useUI } from '@/state/store';
import { ChatChannels } from '@shared/protocol';

const WS_URL_BASE =
  import.meta.env.VITE_WS_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'ws://localhost:8787'
    : 'wss://virtuaroo-server.michaeljoiner.workers.dev');

async function boot(): Promise<void> {
  const canvas = document.getElementById('virtuaroo-canvas');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('Virtuaroo: missing #virtuaroo-canvas element.');
  }

  // Loading screen first
  new LoadingScreen();
  mountDisclaimer();

  const renderer = new Renderer(canvas, { wsUrlBase: WS_URL_BASE });
  await renderer.init();
  renderer.start();

  // Expose for debugging in DevTools.
  const { festivalClock } = await import('@/core/Time');
  (window as unknown as { __virtuaroo: unknown; __festivalClock: unknown }).__virtuaroo = renderer;
  (window as unknown as { __festivalClock: unknown }).__festivalClock = festivalClock;

  // Spawn picker
  const picker = new SpawnPicker({
    onSpawn: async (zoneId, name, hatColor) => {
      useUI.getState().setIdentity({ displayName: name, hatColor });
      // Unlock audio context on this user gesture.
      await renderer.audio.unlock();
      renderer.spawnInto(zoneId);
      mountInGameUI(renderer);
      new Onboarding().show();
      // Auto-engage pointer lock so the user can immediately look around.
      setTimeout(() => renderer.input.engagePointerLock(), 100);
    }
  });
  picker.mount();
}

function mountInGameUI(renderer: Renderer): void {
  const chat = new ChatPanel({
    onSend: (text) => {
      renderer.net.sendChat(ChatChannels.Global, text);
    },
    onClose: () => {
      renderer.input.setChatOpen(false);
    }
  });

  const openMap = (): void => {
    const warpPicker = new SpawnPicker({
      onSpawn: (zoneId) => {
        renderer.spawnInto(zoneId);
      }
    });
    warpPicker.mount();
  };

  new HUD({
    onEmote: (anim) => {
      renderer.player.emoteUntilMove = anim;
    },
    onOpenChat: () => {
      chat.setOpen(true);
      renderer.input.setChatOpen(true);
    },
    onOpenMap: openMap,
    onToggleMultiplayer: () => {
      if (useUI.getState().connected) {
        renderer.disableMultiplayer();
      } else {
        renderer.enableMultiplayer();
      }
    },
    onToggleCamera: () => {
      renderer.player.cameraMode = renderer.player.cameraMode === 'third' ? 'first' : 'third';
    },
    getCameraMode: () => renderer.player.cameraMode
  });

  // Also let the input system trigger map open via M.
  renderer.input.on((e) => {
    if (e.type === 'open-spawn-picker') openMap();
  });

  const minimap = new Minimap(renderer.net);

  // Hook input events
  renderer.input.on((e) => {
    if (e.type === 'chat-open') {
      chat.setOpen(true);
      renderer.input.setChatOpen(true);
    }
    if (e.type === 'chat-close') {
      chat.setOpen(false);
      renderer.input.setChatOpen(false);
    }
  });

  // Drive the minimap on the render loop via rAF
  const updateMm = (): void => {
    const id = useUI.getState().identity;
    if (id) minimap.update(renderer.player.position, id.hatColor);
    requestAnimationFrame(updateMm);
  };
  requestAnimationFrame(updateMm);
}

boot().catch((err) => {
  // eslint-disable-next-line no-console
  console.error('[virtuaroo] boot failed', err);
  document.body.innerHTML = `<pre style="color:#ff5f6d;padding:24px;font-family:ui-monospace">Virtuaroo failed to boot:\n${String(err)}\n\n${err?.stack ?? ''}</pre>`;
});
